import { useCallback, useEffect, useState } from 'react';
import FishAvatar from '../../components/FishAvatar';
import Loading from '../../components/Loading';
import { cleanLook } from '../../game/avatar';
import { GEAR, type Gear, type GearSlot } from '../../game/gear';
import { sfxCoin, sfxCrack, sfxRare } from '../../game/sfx';
import { RARITY, speciesById, speciesImage, type Rarity } from '../../lib/aquarium';
import { burst } from '../../lib/confetti';
import { saveLook } from '../../lib/ocean';
import { buyEgg, buyGear, EGGS, fetchShop, sandDollars, type ShopData } from '../../lib/shop';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// The shop (/shop): just the shop, nothing else (the games are all under Ocean,
// routes/student/Games.tsx).
//   Eggs         hatch a random fish into your bucket
//   Fish style   hats, glasses and trails for your character fish
// Rules: supabase/migrations/20261028_shop_and_games.sql.
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'eggs' | 'style';
const TABS: { id: Tab; label: string }[] = [{ id: 'eggs', label: '🥚 Eggs' }, { id: 'style', label: '🎩 Fish style' }];

export default function Shop() {
  const { active, session } = useClassroom();
  const me = session?.user.id ?? '';
  const [data, setData] = useState<ShopData | null>(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>(() => { try { const t = sessionStorage.getItem('shop.tab') as Tab; return TABS.some(x => x.id === t) ? t : 'eggs'; } catch { return 'eggs'; } });

  const load = useCallback(async () => {
    if (!active) return;
    try { setData(await fetchShop(active.id)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not open the shop.'); }
  }, [active]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { try { sessionStorage.setItem('shop.tab', tab); } catch { /* fine */ } }, [tab]);

  const setBalance = (balance: number) => setData(d => (d ? { ...d, balance } : d));
  if (!active) return <div className="notice">Join a class to use the shop.</div>;
  if (error && !data) return <div className="notice bad">{error}</div>;
  if (!data) return <Loading what="the shop" />;
  const balance = data.balance ?? 0;

  return (
    <div className="shop">
      <div className="shop-head">
        <h1>🛍️ Shop</h1>
        <span className="shop-balance" aria-label={`${sandDollars(balance)} sand dollars`}>🪙 {sandDollars(balance)}</span>
      </div>
      <div className="seg shop-tabs" role="tablist">
        {TABS.map(t => <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.label}</button>)}
      </div>
      {tab === 'eggs' && <Eggs cid={active.id} balance={balance} setBalance={setBalance} />}
      {tab === 'style' && <Style cid={active.id} me={me} data={data} setData={setData} />}
    </div>
  );
}

// ── Eggs ────────────────────────────────────────────────────────────────────
function Eggs({ cid, balance, setBalance }: { cid: string; balance: number; setBalance: (b: number) => void }) {
  const [hatching, setHatching] = useState<string | null>(null);
  const [hatched, setHatched] = useState<{ species: string; rarity: Rarity; new: boolean } | null>(null);
  const [error, setError] = useState('');

  const buy = async (egg: string) => {
    setError(''); setHatched(null); setHatching(egg);
    const wobble = [0, 450, 900].map(ms => window.setTimeout(sfxCrack, ms));
    try {
      const [r] = await Promise.all([buyEgg(cid, egg), new Promise(res => window.setTimeout(res, 1400))]);
      setBalance(r.balance);
      setHatched({ species: r.species, rarity: r.rarity as Rarity, new: r.new });
      if (['rare', 'epic', 'legendary', 'mythic'].includes(r.rarity)) { sfxRare(); burst(); } else sfxCoin();
    } catch (e) {
      wobble.forEach(id => window.clearTimeout(id));
      setError(e instanceof Error ? e.message : 'The egg wouldn’t hatch.');
    }
    setHatching(null);
  };

  if (hatched) {
    const sp = speciesById(hatched.species);
    return (
      <div className="panel egg-reveal" style={{ borderColor: RARITY[hatched.rarity].color }}>
        <img src={speciesImage(sp.id)} alt="" />
        <h2>{sp.name}</h2>
        <div className="rarity-tag" style={{ background: RARITY[hatched.rarity].color }}>{RARITY[hatched.rarity].name}</div>
        {hatched.new && <p><strong>📖 New in your Fishpedia!</strong></p>}
        <p className="muted">It’s swimming in your bucket. Put it in a fish house in the Ocean.</p>
        <button type="button" className="primary" onClick={() => setHatched(null)}>Hatch another</button>
      </div>
    );
  }

  return (
    <>
      <div className="egg-grid">
        {EGGS.map(e => (
          <div key={e.id} className="panel egg-card">
            <div className={`egg egg-${e.id} ${hatching === e.id ? 'wobble' : ''}`} aria-hidden />
            <h3>{e.name}</h3>
            <p className="small muted">{e.blurb}</p>
            <button type="button" className="primary" disabled={!!hatching || balance < e.cost} onClick={() => void buy(e.id)}>
              {hatching === e.id ? 'Hatching…' : `Hatch · 🪙 ${e.cost}`}
            </button>
          </div>
        ))}
      </div>
      {error && <div className="notice bad" role="alert">{error}</div>}
      <p className="small muted">Hatched fish go in your bucket even when it’s full, and count in your Fishpedia.</p>
    </>
  );
}

// ── Fish style ──────────────────────────────────────────────────────────────
function Style({ cid, me, data, setData }: { cid: string; me: string; data: ShopData; setData: (f: (d: ShopData | null) => ShopData | null) => void }) {
  const look = data.look ? cleanLook(data.look) : null;
  const worn: Gear = look?.gear ?? {};
  const [trying, setTrying] = useState<Gear>({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const owned = new Set(data.gear);
  const shown: Gear = { ...worn, ...trying };

  if (!look) return <div className="notice">Make your fish in the <strong>Ocean</strong> first, then come back to dress it up.</div>;

  const wear = async (slot: GearSlot, id: string | undefined) => {
    const gear: Gear = { ...worn, [slot]: id };
    if (!id) delete gear[slot];
    const next = { ...look, gear };
    setBusy(id ?? slot); setError('');
    try {
      await saveLook(me, next);
      setData(d => (d ? { ...d, look: next } : d));
      setTrying(t => { const c = { ...t }; delete c[slot]; return c; });
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save.'); }
    setBusy('');
  };
  const buy = async (id: string, slot: GearSlot) => {
    setBusy(id); setError('');
    try {
      const r = await buyGear(cid, id);
      sfxCoin();
      setData(d => (d ? { ...d, balance: r.balance, gear: [...d.gear, id] } : d));
      setBusy('');
      await wear(slot, id);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not buy that.'); setBusy(''); }
  };

  const balance = data.balance ?? 0;
  return (
    <div className="style-shop">
      <div className="panel style-preview">
        <FishAvatar look={{ ...look, gear: shown }} id={me} size={130} excited />
        {Object.keys(trying).length > 0 && (
          <button type="button" className="small" onClick={() => setTrying({})}>Stop trying on</button>
        )}
      </div>
      <div className="style-lists">
        {(['hat', 'face', 'trail'] as GearSlot[]).map(slot => (
          <section key={slot}>
            <h3>{slot === 'hat' ? 'Hats' : slot === 'face' ? 'Face' : 'Trails'}</h3>
            <div className="gear-grid">
              {GEAR.filter(g => g.slot === slot).map(g => {
                const has = owned.has(g.id), on = worn[slot] === g.id;
                return (
                  <div key={g.id} className={`gear-card ${on ? 'on' : ''} ${trying[slot] === g.id ? 'trying' : ''}`}>
                    <button type="button" className="gear-try" onClick={() => setTrying(t => ({ ...t, [slot]: g.id }))} aria-label={`Try on the ${g.name}`}>
                      <span className="gear-icon" aria-hidden>{g.icon}</span>
                      <span>{g.name}</span>
                    </button>
                    {has
                      ? <button type="button" className={on ? '' : 'primary'} disabled={!!busy} onClick={() => void wear(slot, on ? undefined : g.id)}>{on ? 'Take off' : 'Wear'}</button>
                      : <button type="button" className="primary" disabled={!!busy || balance < g.cost} onClick={() => void buy(g.id, slot)}>{busy === g.id ? '…' : `🪙 ${g.cost}`}</button>}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {error && <div className="notice bad" role="alert">{error}</div>}
        <p className="small muted">Tap an item to try it on. What you buy is yours for good, in every class, and classmates see it on your fish.</p>
      </div>
    </div>
  );
}
